/**
 * Every third-party photograph the site shows, with what its licence asks of us.
 *
 * WHY THIS FILE EXISTS. CC BY and CC BY-SA are permission on a condition: the
 * photo must be credited where people can see it. The credits render on the
 * About page at /about#photo-credits (the footer links there), straight from
 * this list. A photograph that is not listed here must not ship.
 *
 * Every entry was checked against its Wikimedia Commons file page on 2026-10-03
 * (API: action=query&prop=imageinfo&iiprop=extmetadata): author and licence are
 * copied from the source, not from memory; licence links are the canonical
 * creativecommons.org deeds for those licences.
 *
 * Adding or replacing a photo:
 *   1. Confirm the licence on the source. Only licences that allow reuse AND
 *      adaptation qualify (CC0, CC BY, CC BY-SA); never NC, ND or "all rights
 *      reserved".
 *   2. City cards: build the file with `node scripts/prep-city-photo.mjs`. A crop
 *      or resize is an adaptation, so the entry gets `edited: true`.
 *   3. Add the entry below, its `about.credits.subjects.<subject>` key in BOTH
 *      i18n/local/ar/about.ts and en/about.ts, and the row in
 *      src/assets/README.md.
 */
import { FEATURED_CITY_SLUGS, type FeaturedCitySlug } from './saudiCities';
import makkahPhoto from '../assets/cities/makkah.webp';
import madinahPhoto from '../assets/cities/madinah.webp';
import riyadhPhoto from '../assets/cities/riyadh.webp';
import jeddahPhoto from '../assets/cities/jeddah.webp';
import dammamPhoto from '../assets/cities/dammam.webp';

/** What the photo shows; also its i18n key under `about.credits.subjects`. */
export type PhotoSubject = FeaturedCitySlug | 'hero';

/** Where the photo appears; its i18n key under `about.credits.usedOn`. */
export type PhotoUsage = 'cityCard' | 'hero';

export interface PhotoCredit {
  subject: PhotoSubject;
  usedOn: PhotoUsage;
  /** The shipped file, relative to src/assets (matches the README manifest). */
  file: string;
  /** Bundled URL of the shipped file: the card image and the credits thumbnail. */
  src: string;
  /** Pixel size of the shipped file (lets the <img> reserve its space). */
  width: number;
  height: number;
  /** The original work's title, as its source page gives it. */
  title: string;
  author: string;
  /** The original's page. Every current photo is from Wikimedia Commons. */
  sourceUrl: string;
  /** Licence short name exactly as the source states it. */
  licence: string;
  licenceUrl: string;
  /** Our file is an adaptation (cropped, resized or colour-adjusted). Required by CC BY 4.0 / BY-SA 4.0. */
  edited: boolean;
}

const CC_BY_SA_4 = { licence: 'CC BY-SA 4.0', licenceUrl: 'https://creativecommons.org/licenses/by-sa/4.0/' } as const;

/** The home page's city cards, keyed by our city slug (config/saudiCities). */
export const CITY_PHOTOS: Readonly<Record<FeaturedCitySlug, PhotoCredit>> = {
  makkah: {
    subject: 'makkah',
    usedOn: 'cityCard',
    file: 'cities/makkah.webp',
    src: makkahPhoto,
    width: 800,
    height: 600,
    title: 'Masjid al-Haram, Tawaf 20092012 1130PM (8008466944)',
    author: 'Basheer Olakara',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Masjid_al-Haram,_Tawaf_20092012_1130PM_(8008466944).jpg',
    licence: 'CC BY 2.0',
    licenceUrl: 'https://creativecommons.org/licenses/by/2.0/',
    edited: true,
  },
  madinah: {
    subject: 'madinah',
    usedOn: 'cityCard',
    file: 'cities/madinah.webp',
    src: madinahPhoto,
    width: 800,
    height: 600,
    title: "Al-Masjid An-Nabawi (Bird's Eye View)",
    // Uploaded to Commons from Pixabay; the CC0 dedication was confirmed by a
    // Commons licence reviewer on 2019-02-17. CC0 asks for no credit; we give
    // one anyway, like every other photo here.
    author: 'Konevi',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Al-Masjid_An-Nabawi_(Bird%27s_Eye_View).jpg',
    licence: 'CC0 1.0',
    licenceUrl: 'https://creativecommons.org/publicdomain/zero/1.0/',
    edited: true,
  },
  riyadh: {
    subject: 'riyadh',
    usedOn: 'cityCard',
    file: 'cities/riyadh.webp',
    src: riyadhPhoto,
    width: 800,
    height: 600,
    title: 'Riyadh North Skyline',
    author: 'B.alotaby',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Riyadh_North_Skyline_.jpg',
    ...CC_BY_SA_4,
    edited: true,
  },
  jeddah: {
    subject: 'jeddah',
    usedOn: 'cityCard',
    file: 'cities/jeddah.webp',
    src: jeddahPhoto,
    width: 800,
    height: 600,
    title: 'Jeddah Corniche 12',
    author: 'Tahir mq',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Jeddah_Corniche_12.jpg',
    ...CC_BY_SA_4,
    edited: true,
  },
  dammam: {
    subject: 'dammam',
    usedOn: 'cityCard',
    file: 'cities/dammam.webp',
    src: dammamPhoto,
    width: 800,
    height: 600,
    title: 'Dammam Corniche (1)',
    author: 'Radosław Botev',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Dammam_Corniche_(1).jpg',
    licence: 'CC BY 3.0 PL',
    licenceUrl: 'https://creativecommons.org/licenses/by/3.0/pl/deed.en',
    edited: true,
  },
};

/**
 * Display order on /about#photo-credits: the city cards in the home page's order.
 *
 * The home hero is not listed: since 2026-10-03 it is a generated image made for
 * Safaqat 24, with no third-party licence to honour. If a licensed photograph
 * ever goes back into the hero, give it a `usedOn: 'hero'` entry here (first in
 * this list) and pass the same entry as HomeHero's `credit`.
 */
export const PHOTO_CREDITS: readonly PhotoCredit[] = FEATURED_CITY_SLUGS.map((slug) => CITY_PHOTOS[slug]);

/** The anchor the footer's «حقوق الصور» link points at: /about#photo-credits. */
export const PHOTO_CREDITS_ANCHOR = 'photo-credits';
